import { useMemo, useState } from 'react';
import { AppUser, OperatorRole } from '../../pm/types';
import { OPERATOR_ROLE_LABELS, OPERATOR_ROLE_ORDER, displayName } from '../../pm/constants';
import { grantOperator } from '../../pm/firebase';
import UserAvatar from './UserAvatar';

interface Props {
  allUsers: AppUser[];
  onClose: () => void;
}

const InviteOperatorModal: React.FC<Props> = ({ allUsers, onClose }) => {
  const [query, setQuery] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [role, setRole] = useState<OperatorRole>('engineer');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allUsers
      .filter((u) => !u.isOperator) // already-operators can't be re-invited
      .filter((u) => !q || u.email.toLowerCase().includes(q) || displayName(u.email).toLowerCase().includes(q))
      .slice(0, 8);
  }, [allUsers, query]);

  async function handleInvite() {
    if (!selectedUserId) {
      setError('Select a user first.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await grantOperator(selectedUserId, role, title);
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Could not grant operator access.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="pm-modal-overlay pm-app" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="pm-modal" role="dialog" aria-modal="true">
        <div className="pm-modal-header">
          <h3>Invite teammate</h3>
          <button className="pm-link-button" onClick={onClose} aria-label="Close">Close</button>
        </div>
        <div className="pm-modal-body">
          <label className="pm-modal-label">User</label>
          <input
            type="text"
            className="pm-modal-input"
            placeholder="Search by name or email…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          <div className="pm-invite-list">
            {candidates.length === 0 ? (
              <div className="pm-invite-empty">No non-operator users match.</div>
            ) : (
              candidates.map((u) => (
                <button
                  key={u.uid}
                  type="button"
                  className={`pm-invite-item ${selectedUserId === u.uid ? 'selected' : ''}`}
                  onClick={() => setSelectedUserId(u.uid)}
                >
                  <UserAvatar email={u.email} size={28} />
                  <div className="pm-invite-item-info">
                    <div className="pm-invite-item-name">{displayName(u.email)}</div>
                    <div className="pm-invite-item-email">{u.email}</div>
                  </div>
                </button>
              ))
            )}
          </div>

          <label className="pm-modal-label">Operator role</label>
          <div className="pm-role-options">
            {OPERATOR_ROLE_ORDER.map((r) => (
              <button
                key={r}
                type="button"
                className={`pm-role-option ${role === r ? 'active' : ''}`}
                onClick={() => setRole(r)}
              >
                {OPERATOR_ROLE_LABELS[r]}
              </button>
            ))}
          </div>

          <label className="pm-modal-label">Title (optional)</label>
          <input
            type="text"
            className="pm-modal-input"
            placeholder="e.g. Founding Engineer"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          {error ? <div className="pm-error" style={{ marginTop: 12 }}>{error}</div> : null}
        </div>
        <div className="pm-modal-footer">
          <button type="button" className="pm-button" onClick={onClose} disabled={busy}>Cancel</button>
          <button
            type="button"
            className="pm-button pm-button-primary"
            onClick={handleInvite}
            disabled={busy || !selectedUserId}
          >
            {busy ? 'Adding…' : 'Add teammate'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InviteOperatorModal;
